// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx

import { Text } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { ChatChipStatic } from "../../components/chat-citation/ChatChipFace";
import { formatChatMeta } from "../../components/sidebar/recentsRowParts";
import {
  SIDEBAR_ITEM_META_CLASS,
  SIDEBAR_ITEM_META_STYLE,
} from "../../components/sidebar/sidebarItemStyles";
import * as AskPunttService from "../../services/askPuntt";
import {
  evidenceFocusKey,
  useEvidenceFocus,
  useEvidenceLitHandlers,
} from "../../utils/memoryCitationFocus";
import { ASK_V2_CHAT_PATH } from "../paths";

/**
 * One conversation the answer references, as a nav row.
 *
 * why: the graph label is the day the chat happened, never its name. The chat
 * record is what the nav shows. A failed read keeps the graph label, so a
 * conversation this person cannot open stays unnamed.
 * why: the Chat badge is the in-text citation, so a conversation the graph
 * only mentions keeps the title, with no number.
 */
export function AskV2RailChatCard({
  chatId,
  title,
  number,
  scope,
}: {
  chatId: string;
  title: string;
  number?: number;
  scope: string;
}) {
  const { t } = useTranslation("ui");
  const key = evidenceFocusKey("chat", scope, chatId);
  const { lit } = useEvidenceFocus(key);
  const litHandlers = useEvidenceLitHandlers(key);
  const { data } = useQuery({
    queryKey: ["ask-v2-rail-cited-chat", chatId],
    queryFn: () => AskPunttService.getChatHistory(chatId),
    retry: false,
    staleTime: 30_000,
  });
  const live = data?.chat;
  const liveTitle = live?.title?.trim() || "";
  const name = liveTitle || title || t("askV2.rail.citedChatUntitled");
  // ownerName is omitted on the requester's own chat, which is how the nav
  // decides whether the date line names someone else.
  const meta = liveTitle
    ? formatChatMeta(live?.updatedAt, live?.ownerName)
    : "";
  return (
    <li>
      <RouterLink
        to={ASK_V2_CHAT_PATH}
        params={{ chatId }}
        data-testid={`ask-v2-rail-cited-chat-${chatId}`}
        aria-label={t("askV2.rail.citedChatAria", { title: name })}
        className="puntt-chat-card flex w-full min-w-0 items-center gap-2 rounded-md py-1.5 no-underline transition-colors hover:bg-puntt-neutral-gray-3"
        data-lit={lit ? "true" : "false"}
        {...litHandlers}
      >
        {number != null ? (
          <ChatChipStatic
            number={number}
            testId={`ask-v2-rail-chat-card-${chatId}-number`}
          />
        ) : null}
        <span className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <Text
            as="span"
            size="2"
            weight="medium"
            className="min-w-0 truncate text-puntt-neutral-gray-12"
          >
            {name}
          </Text>
          {meta ? (
            <span
              className={SIDEBAR_ITEM_META_CLASS}
              style={SIDEBAR_ITEM_META_STYLE}
              data-testid={`ask-v2-rail-cited-chat-${chatId}-meta`}
            >
              {meta}
            </span>
          ) : null}
        </span>
      </RouterLink>
    </li>
  );
}
